import * as React from 'react';
import Flag from 'react-world-flags';

import { Box, CircularProgress, Grid } from '@mui/material';

import { Formik } from 'formik';
import { isEqual } from 'lodash';
import { useSnackbar } from 'notistack';

import { venueUpdateVenueApi } from '../../../../api/venue/venueUpdateVenueApi';
import { VenueUpdateVenueInput } from '../../../../graphql/API';
import { fetchVenue } from '../../../../redux/features/venue/venueSlice';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { VENUE_MANUAL_TAGS } from '../../../../utils/venue-tags';
import Button from '../../../Button/Button';
import SelectOption from '../../../Formiks/SelectOption/SelectOption';
import TextField from '../../../Formiks/TextField/TextField';
import countries from '../countries.json';
import GooglePlaceContainer from './GooglePlaceContainer';
import './ProfileUpdate.css';
import { makeFormValue, onlineBrandSchema, schema } from './schema';

type ProfileUpdateProps = {
  onSaveComplete?: () => void;
};

type ProfileUpdateFormValues = VenueUpdateVenueInput & {
  tiktokHandle?: string | null;
};

const countriesOptions = countries.map((country) => ({
  value: country.name,
  label: (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
      <Flag width={'20px'} height={'20px'} code={country.code} fallback={<span>Unknown</span>} />
      {country.name}
    </Box>
  ),
}));

const Field: React.FC<React.PropsWithChildren<{ label: string }>> = ({ label, children }) => (
  <Box>
    <label className='update-profile-modal-label'>{label}</label>
    {children}
  </Box>
);

const ProfileUpdate: React.FC<ProfileUpdateProps> = ({ onSaveComplete }) => {
  const venueState = useAppSelector((state) => state.venue.value);
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const initialValues = React.useMemo<ProfileUpdateFormValues | undefined>(
    () => makeFormValue(venueState) as ProfileUpdateFormValues | undefined,
    [venueState],
  );

  const isOnlineBrand = venueState?.tags?.includes(VENUE_MANUAL_TAGS.Online);

  return initialValues ? (
    <Formik<ProfileUpdateFormValues>
      initialValues={initialValues}
      validationSchema={isOnlineBrand ? onlineBrandSchema : schema}
      onSubmit={async (values) => {
        if (!isEqual(initialValues, values)) {
          const { tiktokHandle, ...venueFields } = values;

          try {
            await venueUpdateVenueApi({
              ...venueFields,
              description: venueFields.description?.trim() || null,
              ...(tiktokHandle !== initialValues.tiktokHandle && {
                tiktokHandle: tiktokHandle || null,
              }),
              countryCode: countries.find((x) => x.name === venueFields.country)?.code,
              location: isOnlineBrand ? undefined : venueFields.location,
            } as VenueUpdateVenueInput);

            dispatch(fetchVenue());
          } catch (err) {
            const message =
              err instanceof Error && err.message ? err.message : 'Failed to update profile';
            enqueueSnackbar(message, { variant: 'error' });
            return;
          }
        }

        onSaveComplete && onSaveComplete();
      }}
    >
      {({ handleSubmit, isSubmitting }) => (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            py: 2,
          }}
          component='form'
          onSubmit={handleSubmit}
        >
          <Grid container spacing={2} width='100%'>
            <Grid item xs={12} sm={6}>
              <Field label='Name'>
                <TextField id='txt-name' name='name' />
              </Field>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Field label='Phone number'>
                <TextField id='txt-phone' name='phone' />
              </Field>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Field label='Website'>
                <TextField id='txt-website' name='website' />
              </Field>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Field label='TikTok handle'>
                <TextField id='txt-tiktok' name='tiktokHandle' placeholder='@yourhandle' />
              </Field>
            </Grid>

            {!isOnlineBrand && (
              <>
                <Grid item xs={12}>
                  <GooglePlaceContainer />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field label='Address 1'>
                    <TextField id='txt-address-1' name='address1' />
                  </Field>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field label='Address 2'>
                    <TextField id='txt-address-2' name='address2' />
                  </Field>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field label='City'>
                    <TextField id='txt-city' name='city' />
                  </Field>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field label='State'>
                    <TextField id='txt-state' name='stateProvince' />
                  </Field>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field label='Zip Code'>
                    <TextField id='txt-postal-code' name='postalCode' />
                  </Field>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field label='Country'>
                    <TextField id='txt-country' name='country' />
                  </Field>
                </Grid>
              </>
            )}

            {isOnlineBrand && (
              <Grid item xs={12}>
                <Field label='Country'>
                  <SelectOption
                    name='country'
                    id='txt-country'
                    options={countriesOptions}
                    fullWidth
                  />
                </Field>
              </Grid>
            )}

            <Grid item xs={12}>
              <Field label='Description'>
                <TextField
                  id='txt-desc'
                  placeholder='Add a Description (optional)'
                  name='description'
                  multiline
                  minRows={3}
                />
              </Field>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Field label='Contact first name'>
                <TextField id='txt-firstname' name='contactFirstname' />
              </Field>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Field label='Contact last name'>
                <TextField id='txt-lastname' name='contactLastname' />
              </Field>
            </Grid>
            <Grid item xs={12}>
              <Field label='Position in company'>
                <TextField id='txt-position' name='contactPosition' />
              </Field>
            </Grid>
          </Grid>

          <Button
            id='btn-save'
            variant='contained'
            size='large'
            type='submit'
            disabled={isSubmitting}
            startIcon={isSubmitting && <CircularProgress size='1rem' sx={{ color: 'inherit' }} />}
            sx={{ mt: 4, px: 10, alignSelf: 'stretch' }}
          >
            Save
          </Button>
        </Box>
      )}
    </Formik>
  ) : null;
};

export default ProfileUpdate;
